import {
    getAsRepairFieldDisplayValue,
    getAsRepairInboundBreakdownFields,
    normalizeAsRepairFormConfig,
    parseQuantityValue,
} from './asRepairFormConfig';

/** 무상수리 — 상위/하위 중첩 구조 (개인 관리 그래프) */
export const AS_REPAIR_FREE_NESTED_GROUPS = [
    { title: '전원불량', children: ['COIL간섭', 'TRANS', 'SMPS', '퓨즈교체', '전원IC교체'] },
    { title: '터치불량', children: ['터치IC교체', '터치가스켓없음', '터치가스켓눌림'] },
    { title: '소리불량', children: ['스피커교체', '스피커냉땜'] },
    { title: '홈넷불량', children: ['홈넷IC교체', '홈넷TR교체'] },
    { title: '보일러불량', children: ['보일러IC교체', '보일러TR교체'] },
    { title: '에어컨불량', children: ['에어컨IC교체', '에어컨TR교체'] },
    { title: '서브PCB불량', children: ['서브PCB분실', '서브PCB펌웨어'] },
    { title: 'Micom교체', children: ['Micom고장', '먹통현상', '펌웨어업데이트'] },
    { title: 'LCD고장', children: ['세그먼트불량', 'LCD냉땜'] },
    { title: '릴레이교체', children: ['외부충격', '릴레이고장'] },
    { title: '통신불량', children: ['통신TR교체', '통신저항교체'] },
    { title: '세팅불량', children: ['핀설정불량', '점퍼분실'] },
    { title: '커넥터교체', children: ['홈넷커넥터', '조절기커넥터', '보일러커넥터', '에어컨커넥터', '타사커넥터'] },
];

/** 수량부족 / 반품 / 유상 / 무상 하위 수량 항목 (A/S 설정 필드 라벨과 매칭) */
export const AS_REPAIR_QTY_DETAIL_CATEGORIES = {
    totalQty: ['전체수량'],
    shortage: ['수량부족'],
    return: ['폐기제품', '수리기간초과'],
    paid: [
        '정상수량',
        '스크레치',
        '이물질',
        'LCD',
        'Micom파손',
        '다이얼',
        '온도센서',
        '커넥터',
        '케이블',
        '미세먼지',
    ],
    free: AS_REPAIR_FREE_NESTED_GROUPS.map((group) => group.title),
    done: ['수리완료수량'],
};

const ALL_FREE_NESTED_CHILD_TITLES = AS_REPAIR_FREE_NESTED_GROUPS.flatMap((g) => g.children);

const normalizeCategoryToken = (text) => (
    String(text || '')
        .replace(/\s+/g, '')
        .replace(/[·・]/g, '')
        .toLowerCase()
);

const PAID_NORMAL_CATEGORY = '정상수량';
const FREE_NORMAL_OPERATION_CATEGORY = '작동정상';
const FREE_NORMAL_OPERATION_FIELD_TOKENS = new Set([
    '작동정상',
    '정상작동',
    '정상동작',
]);

export const matchAsRepairCategoryToFieldLabel = (fieldLabel, categoryTitle) => {
    const fl = normalizeCategoryToken(fieldLabel);
    const ct = normalizeCategoryToken(categoryTitle);
    if (!fl || !ct) {
        return false;
    }
    if (fl === ct) {
        return true;
    }
    // 유상「정상수량」: 라벨이 정확히 정상류인 필드만 (무상 작동정상과 분리)
    if (ct === normalizeCategoryToken(PAID_NORMAL_CATEGORY)) {
        return fl === normalizeCategoryToken(PAID_NORMAL_CATEGORY)
            || fl === normalizeCategoryToken('정상');
    }
    // 무상「작동정상」: 동작·작동 표기 모두 허용, 유상 정상류와 분리
    if (ct === normalizeCategoryToken(FREE_NORMAL_OPERATION_CATEGORY)) {
        return FREE_NORMAL_OPERATION_FIELD_TOKENS.has(fl);
    }
    // 무상「COIL간섭」: COIL간섭·COIL 간섭·COIL 계열 필드 매칭
    if (ct === normalizeCategoryToken('COIL간섭') || ct === normalizeCategoryToken('COIL')) {
        return fl.includes('coil');
    }
    // 무상「전원IC교체」(구 7805고장): 전원IC교체·7805고장 등
    if (ct === normalizeCategoryToken('전원IC교체')) {
        return fl === normalizeCategoryToken('전원IC교체')
            || (fl.includes('7805') && fl.includes('고장'));
    }
    // 유상「Micom파손」: 파손만 매칭 (무상 Micom고장과 분리)
    if (ct === normalizeCategoryToken('Micom파손')) {
        const hasMicom = fl.includes('micom') || fl.includes('마이컴') || fl.includes('마이콤');
        return hasMicom && fl.includes('파손') && !fl.includes('고장');
    }
    // 무상「Micom고장」: 고장만 매칭 (유상 Micom파손·Micom교체 상위와 분리)
    if (ct === normalizeCategoryToken('Micom고장')) {
        const hasMicom = fl.includes('micom') || fl.includes('마이컴') || fl.includes('마이콤');
        return hasMicom && fl.includes('고장') && !fl.includes('파손') && !fl.includes('교체');
    }
    // 무상「Micom교체」상위: 라벨이 Micom교체인 필드만 (하위 Micom고장과 분리)
    if (ct === normalizeCategoryToken('Micom교체')) {
        const hasMicom = fl.includes('micom') || fl.includes('마이컴') || fl.includes('마이콤');
        return hasMicom && fl.includes('교체') && !fl.includes('고장');
    }
    // LCD고장 하위「LCD냉땜」: LCD냉땜·냉땜(LCD)만 (스피커냉땜 제외)
    if (ct === normalizeCategoryToken('LCD냉땜')) {
        return fl === 'lcd냉땜' || fl === '냉땜';
    }
    return fl.includes(ct) || ct.includes(fl);
};

const fieldMatchesCategory = (field, categoryTitle) => (
    matchAsRepairCategoryToFieldLabel(field?.label, categoryTitle)
    || matchAsRepairCategoryToFieldLabel(field?.key, categoryTitle)
    || matchAsRepairCategoryToFieldLabel(
        String(field?.key || '').replace(/^custom_/i, ''),
        categoryTitle,
    )
);

const sumQtyAcrossRows = (rows, field, config) => {
    if (!field) {
        return 0;
    }
    const list = Array.isArray(rows) ? rows : [];
    return list.reduce(
        (sum, row) => sum + parseQuantityValue(
            getAsRepairFieldDisplayValue(field, row, config) ?? 0,
        ),
        0,
    );
};

const isDetailCategoryField = (fieldOrLabel) => {
    const allTitles = [
        ...Object.values(AS_REPAIR_QTY_DETAIL_CATEGORIES).flat(),
        ...ALL_FREE_NESTED_CHILD_TITLES,
    ];
    if (fieldOrLabel && typeof fieldOrLabel === 'object') {
        return allTitles.some((title) => fieldMatchesCategory(fieldOrLabel, title));
    }
    return allTitles.some((title) => matchAsRepairCategoryToFieldLabel(fieldOrLabel, title));
};

const collectExcludeKeysForDetailMatch = (config, listSummary) => {
    const fields = normalizeAsRepairFormConfig(config).fields;
    const {
        receivedField,
        shortageField,
        returnField,
        paidField,
        freeField,
    } = getAsRepairInboundBreakdownFields(
        config,
        listSummary,
    );
    const exclude = new Set();
    [receivedField, shortageField, returnField, paidField, freeField].forEach((f) => {
        if (f?.key) {
            exclude.add(f.key);
        }
    });
    (listSummary?.columns || []).forEach((col) => {
        if (col.id === 'inProgressQty' || col.id === 'doneQty') {
            if (col.field?.key) {
                exclude.add(col.field.key);
            }
        }
    });
    fields.forEach((f) => {
        if (f.inputType !== 'quantity' && f.inputType !== 'auto') {
            return;
        }
        if (exclude.has(f.key) || isDetailCategoryField(f)) {
            return;
        }
        const nl = normalizeCategoryToken(f.label);
        if (
            nl.includes('전체')
            || nl.includes('실입고')
            || nl.includes('진행중')
            || nl.includes('수리완료')
            || nl.includes('완료수량')
            || nl.includes('부족')
            || (nl.includes('반품') && !nl.includes('폐기'))
            || nl.includes('유상수리')
            || nl.includes('무상수리')
            || (nl.includes('입고') && !nl.includes('폐기'))
        ) {
            exclude.add(f.key);
        }
    });
    return exclude;
};

const assignFieldsToCategories = (fields, categories, excludeKeys) => {
    const pool = fields.filter(
        (f) => (f.inputType === 'quantity' || f.inputType === 'auto') && !excludeKeys.has(f.key),
    );
    const sortedCategories = [...categories].sort(
        (a, b) => normalizeCategoryToken(b).length - normalizeCategoryToken(a).length,
    );
    const assignedFieldKeys = new Set();
    const map = {};
    sortedCategories.forEach((cat) => {
        map[cat] = [];
    });

    pool.forEach((field) => {
        if (assignedFieldKeys.has(field.key)) {
            return;
        }
        const cat = sortedCategories.find((title) => fieldMatchesCategory(field, title));
        if (cat) {
            map[cat].push(field);
            assignedFieldKeys.add(field.key);
        }
    });

    // 상위 집계 필드로 제외된 경우에도 세부 카테고리(Micom고장 등)는 다시 연결
    const excludedPool = fields.filter(
        (f) => (f.inputType === 'quantity' || f.inputType === 'auto')
            && excludeKeys.has(f.key)
            && !assignedFieldKeys.has(f.key),
    );
    excludedPool.forEach((field) => {
        if (assignedFieldKeys.has(field.key)) {
            return;
        }
        const cat = sortedCategories.find((title) => fieldMatchesCategory(field, title));
        if (cat) {
            map[cat].push(field);
            assignedFieldKeys.add(field.key);
        }
    });

    return map;
};

/** 수량 필드 값이 0보다 큰 행 ID 목록 */
export const collectRowIdsForQtyField = (rows, config, field) => {
    if (!field) {
        return [];
    }
    const list = Array.isArray(rows) ? rows : [];
    const ids = [];
    list.forEach((row) => {
        const qty = parseQuantityValue(
            getAsRepairFieldDisplayValue(field, row, config) ?? 0,
        );
        if (qty > 0 && row?.id != null) {
            ids.push(row.id);
        }
    });
    return [...new Set(ids)];
};

/** 여러 수량 필드 중 하나라도 0보다 크면 포함 */
export const collectRowIdsForQtyFields = (rows, config, fields) => {
    const idSet = new Set();
    (Array.isArray(fields) ? fields : []).forEach((field) => {
        collectRowIdsForQtyField(rows, config, field).forEach((id) => idSet.add(id));
    });
    return [...idSet];
};

/** 수량부족/반품/유상/무상 그룹에 해당하는 행 ID */
export const collectRowIdsForQtyGroup = (rows, config, listSummary, groupId) => {
    const {
        receivedField,
        doneField,
        shortageField,
        returnField,
        paidField,
        freeField,
    } = getAsRepairInboundBreakdownFields(
        config,
        listSummary,
    );
    const field = groupId === 'totalQty'
        ? receivedField
        : groupId === 'shortage'
            ? shortageField
            : groupId === 'done'
                ? doneField
                : groupId === 'return'
                    ? returnField
                    : groupId === 'paid'
                        ? paidField
                        : freeField;
    return collectRowIdsForQtyField(rows, config, field);
};

/** 목록 행의 모델명 (리스트 요약 설정 기준) */
export const getAsRepairRowModelName = (row, listSummary, config) => {
    const modelCol = listSummary?.columns?.find((c) => c.id === 'modelName');
    if (modelCol?.field) {
        const v = getAsRepairFieldDisplayValue(modelCol.field, row, config);
        const text = v == null ? '' : String(v).trim();
        if (text) {
            return text;
        }
    }
    const fallback = row?.productName ?? row?.product_name ?? '';
    const text = String(fallback).trim();
    return text || '미입력';
};

/** 하위 항목(폐기·LCD 등)별 모델명 수량·건수 집계 */
export const aggregateCategoryQtyByModel = (rows, config, listSummary, matchedFields, categoryTotal) => {
    const list = Array.isArray(rows) ? rows : [];
    const fields = Array.isArray(matchedFields) ? matchedFields : [];
    if (fields.length === 0) {
        return { items: [], totalQuantity: 0 };
    }

    const byModel = new Map();
    list.forEach((row) => {
        const rowQty = fields.reduce(
            (sum, field) => sum + parseQuantityValue(
                getAsRepairFieldDisplayValue(field, row, config) ?? 0,
            ),
            0,
        );
        if (rowQty <= 0) {
            return;
        }
        const modelName = getAsRepairRowModelName(row, listSummary, config);
        const prev = byModel.get(modelName) || { quantity: 0, rowCount: 0, rowIds: [] };
        const rowId = row?.id;
        byModel.set(modelName, {
            quantity: prev.quantity + rowQty,
            rowCount: prev.rowCount + 1,
            rowIds: rowId != null ? [...prev.rowIds, rowId] : prev.rowIds,
        });
    });

    const totalQuantity = categoryTotal > 0
        ? categoryTotal
        : Array.from(byModel.values()).reduce((s, v) => s + v.quantity, 0);

    const items = Array.from(byModel.entries())
        .map(([modelName, stats]) => {
            const percentOfCategory = totalQuantity > 0
                ? Math.round((stats.quantity / totalQuantity) * 1000) / 10
                : 0;
            return {
                modelName,
                quantity: stats.quantity,
                rowCount: stats.rowCount,
                rowIds: stats.rowIds || [],
                percentOfCategory,
                percentLabel: totalQuantity > 0 ? `${percentOfCategory}%` : '-',
            };
        })
        .sort((a, b) => (
            b.quantity - a.quantity
            || b.rowCount - a.rowCount
            || a.modelName.localeCompare(b.modelName, 'ko')
        ));

    return { items, totalQuantity };
};

/** 그룹(전체/수량부족/반품/유상/무상/수리완료) 하위 항목 합계 및 상위 수량 대비 % */
export const aggregateAsRepairQtyGroupDetail = (rows, config, listSummary, groupId) => {
    const categories = AS_REPAIR_QTY_DETAIL_CATEGORIES[groupId];
    if (!categories) {
        return { parentLabel: '', parentTotal: 0, items: [] };
    }

    const {
        receivedField,
        doneField,
        shortageField,
        returnField,
        paidField,
        freeField,
    } = getAsRepairInboundBreakdownFields(
        config,
        listSummary,
    );
    const parentField = groupId === 'totalQty'
        ? receivedField
        : groupId === 'shortage'
        ? shortageField
        : groupId === 'done'
            ? doneField
            : groupId === 'return'
                ? returnField
                : groupId === 'paid'
                    ? paidField
                    : freeField;
    const parentTotal = sumQtyAcrossRows(rows, parentField, config);
    const excludeKeys = collectExcludeKeysForDetailMatch(config, listSummary);
    const fields = normalizeAsRepairFormConfig(config).fields;

    // 무상: 상위(전원불량 등) + 하위(COIL간섭 등) 중첩 집계
    if (groupId === 'free') {
        const childTitles = ALL_FREE_NESTED_CHILD_TITLES;
        const parentTitles = AS_REPAIR_FREE_NESTED_GROUPS.map((g) => g.title);
        const childAssignment = assignFieldsToCategories(fields, childTitles, excludeKeys);
        const parentAssignment = assignFieldsToCategories(
            fields,
            parentTitles,
            new Set([
                ...excludeKeys,
                ...Object.values(childAssignment).flat().map((f) => f.key),
            ]),
        );

        const items = AS_REPAIR_FREE_NESTED_GROUPS.map((group) => {
            const childItems = group.children.map((childTitle) => {
                const matchedFields = childAssignment[childTitle] || [];
                const value = matchedFields.reduce(
                    (sum, field) => sum + sumQtyAcrossRows(rows, field, config),
                    0,
                );
                const rowIds = collectRowIdsForQtyFields(rows, config, matchedFields);
                return {
                    id: `free-child-${normalizeCategoryToken(group.title)}-${normalizeCategoryToken(childTitle)}`,
                    title: childTitle,
                    label: matchedFields.length === 1
                        ? matchedFields[0].label
                        : childTitle,
                    value,
                    rowIds,
                    rowCount: rowIds.length,
                    fieldFound: matchedFields.length > 0,
                    matchedFields,
                    modelBreakdown: aggregateCategoryQtyByModel(
                        rows,
                        config,
                        listSummary,
                        matchedFields,
                        value,
                    ),
                };
            });

            const childFields = childItems.flatMap((c) => c.matchedFields || []);
            const parentMatched = parentAssignment[group.title] || [];
            const matchedFields = childFields.length > 0 ? childFields : parentMatched;
            const valueFromChildren = childItems.reduce((sum, c) => sum + c.value, 0);
            const valueFromParent = parentMatched.reduce(
                (sum, field) => sum + sumQtyAcrossRows(rows, field, config),
                0,
            );
            const value = childFields.length > 0 ? valueFromChildren : valueFromParent;
            const percentOfParent = parentTotal > 0
                ? Math.round((value / parentTotal) * 1000) / 10
                : 0;
            const rowIds = collectRowIdsForQtyFields(rows, config, matchedFields);
            const nestedItems = childItems.map((child) => {
                const percentOfNestedParent = value > 0
                    ? Math.round((child.value / value) * 1000) / 10
                    : 0;
                return {
                    ...child,
                    percentOfParent: percentOfNestedParent,
                    percentLabel: value > 0 ? `${percentOfNestedParent}%` : '-',
                };
            });

            return {
                id: `${groupId}-${normalizeCategoryToken(group.title)}`,
                title: group.title,
                label: group.title,
                value,
                rowIds,
                rowCount: rowIds.length,
                percentOfParent,
                percentLabel: parentTotal > 0 ? `${percentOfParent}%` : '-',
                fieldFound: matchedFields.length > 0 || nestedItems.some((c) => c.fieldFound),
                nestedBreakdown: {
                    parentLabel: group.title,
                    parentTotal: value,
                    items: nestedItems,
                },
                modelBreakdown: aggregateCategoryQtyByModel(
                    rows,
                    config,
                    listSummary,
                    matchedFields,
                    value,
                ),
            };
        });

        return {
            parentLabel: '무상수리수량',
            parentTotal,
            items,
            nestedMode: true,
        };
    }

    const assignment = assignFieldsToCategories(fields, categories, excludeKeys);

    const items = categories.map((title) => {
        const matchedFieldsBase = assignment[title] || [];
        const matchedFields = (
            matchedFieldsBase.length === 0
            && categories.length === 1
            && parentField
        )
            ? [parentField]
            : matchedFieldsBase;
        const value = matchedFields.reduce(
            (sum, field) => sum + sumQtyAcrossRows(rows, field, config),
            0,
        );
        const percentOfParent = parentTotal > 0
            ? Math.round((value / parentTotal) * 1000) / 10
            : 0;
        const rowIds = collectRowIdsForQtyFields(rows, config, matchedFields);
        return {
            id: `${groupId}-${normalizeCategoryToken(title)}`,
            title,
            label: matchedFields.length === 1
                ? matchedFields[0].label
                : matchedFields.length > 1
                    ? `${title} (${matchedFields.length}항목)`
                    : title,
            value,
            rowIds,
            rowCount: rowIds.length,
            percentOfParent,
            percentLabel: parentTotal > 0 ? `${percentOfParent}%` : '-',
            fieldFound: matchedFields.length > 0,
            modelBreakdown: aggregateCategoryQtyByModel(
                rows,
                config,
                listSummary,
                matchedFields,
                value,
            ),
        };
    });

    return {
        parentLabel: groupId === 'totalQty'
            ? '전체수량'
            : groupId === 'shortage'
                ? '수량부족'
                : groupId === 'return'
                    ? '반품수량'
                    : groupId === 'paid'
                        ? '유상수리수량'
                        : groupId === 'done'
                            ? '수리완료수량'
                            : '무상수리수량',
        parentTotal,
        items,
    };
};
